// Scannable "service standards" proof strip.
//
// Modelled on what the strongest chauffeur sites in this market do well: a
// short, factual band of operating guarantees and numbers that a buyer can
// scan in five seconds, placed above the long-form copy. No outbound links
// (keeps per-page anchor text unique), no invented review counts.

import { ShieldCheck, Plane, Clock, BadgeCheck, Users, CircleDollarSign } from "lucide-react";

type Item = { icon: typeof ShieldCheck; label: string; detail: string };

const ITEMS: Item[] = [
  {
    icon: CircleDollarSign,
    label: "Flat all-in rates",
    detail: "Quoted and approved before you ride — tolls, parking and standard wait time included.",
  },
  {
    icon: Plane,
    label: "Flight-tracked pickups",
    detail: "Every airport run is monitored live, so delays and early landings cost you nothing.",
  },
  {
    icon: Clock,
    label: "Complimentary wait time",
    detail: "60 minutes on domestic arrivals, 90 on international, before any overage applies.",
  },
  {
    icon: ShieldCheck,
    label: "CPUC-licensed & insured",
    detail: "A fully permitted California charter-party carrier with commercial liability coverage.",
  },
  {
    icon: BadgeCheck,
    label: "Vetted career chauffeurs",
    detail: "Background-checked, drug-tested professionals who drive these corridors daily.",
  },
  {
    icon: Users,
    label: "staffed dispatch",
    detail: "A real person answers at 3 AM — red-eyes, diversions and last-minute changes included.",
  },
];

export function ServiceStandards({
  eyebrow = "Service standards",
  heading,
  intro,
}: {
  eyebrow?: string;
  heading: string;
  intro: string;
}) {
  return (
    <section className="border-y border-border bg-surface" id="service-standards">
      <div className="container-x py-16">
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h2 className="text-2xl md:text-3xl mb-4">{heading}</h2>
        <p className="text-sm text-muted-foreground max-w-3xl mb-8 leading-relaxed">{intro}</p>

        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ITEMS.map(({ icon: Icon, label, detail }) => (
            <li key={label} className="border border-border bg-background p-5">
              <Icon className="h-5 w-5 text-gold mb-3" aria-hidden="true" />
              <p className="font-display text-base mb-1.5">{label}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{detail}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
